import { createContext, useCallback, useContext, useEffect, useRef, useState, PropsWithChildren } from "react";
import { RenderParameters, TurnstileObject } from "turnstile-types";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_PUBLIC_KEY || "";

// Singleton pattern for script loading
let scriptLoading = false;
let scriptLoaded = false;

const globalNamespace = (typeof globalThis !== "undefined" ? globalThis : window) as {
  turnstile?: TurnstileObject;
  onloadTurnstileCallback?: () => void;
};

type PendingExecution = {
  resolve: (token: string | null) => void;
  timeout: NodeJS.Timeout;
};

interface TurnstileContextValue {
  executeTurnstile: () => Promise<string | null>;
  renderTurnstileWidget: (container: HTMLDivElement, renderParameters: RenderParameters) => string | null;
}

const AntiBotVerificationContext = createContext<TurnstileContextValue | null>(null);

export const AntiBotVerificationProvider = ({ children }: PropsWithChildren) => {
  const [isTurnstileLoaded, setIsTurnstileLoaded] = useState(scriptLoaded);
  const widgetIdRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentExecutionRef = useRef<PendingExecution | null>(null);
  const executionQueueRef = useRef<(() => Promise<string | null>)[]>([]);
  const isExecutingRef = useRef(false);

  // Load the Turnstile script - with singleton pattern
  useEffect(() => {
    if (scriptLoaded) {
      setIsTurnstileLoaded(true);
      return;
    }

    if (scriptLoading) return;

    scriptLoading = true;

    globalNamespace.onloadTurnstileCallback = () => {
      scriptLoaded = true;
      scriptLoading = false;
      setIsTurnstileLoaded(true);
    };

    const script = document.createElement("script");
    script.id = "turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadTurnstileCallback";
    script.async = true;

    script.onerror = () => {
      scriptLoading = false;
    };

    document.head.appendChild(script);
  }, []);

  const cleanupExecution = useCallback(() => {
    if (currentExecutionRef.current) {
      clearTimeout(currentExecutionRef.current.timeout);
      currentExecutionRef.current = null;
    }
    isExecutingRef.current = false;
  }, []);

  const onTurnstileResponse = useCallback(
    (token: string | null) => {
      if (currentExecutionRef.current) {
        currentExecutionRef.current.resolve(token);
        cleanupExecution();
      }

      processNextExecution();
    },
    [cleanupExecution],
  );

  const processNextExecution = useCallback(async () => {
    if (isExecutingRef.current || executionQueueRef.current.length === 0) {
      return;
    }

    isExecutingRef.current = true;
    const nextExecution = executionQueueRef.current.shift();
    if (nextExecution) {
      await nextExecution();
    }
  }, []);

  // Initialize container and widget
  useEffect(() => {
    if (!isTurnstileLoaded || containerRef.current || !globalNamespace.turnstile) return;

    const container = document.createElement("div");
    container.id = "turnstile-container-" + Math.random().toString(36).substring(2, 9);
    container.style.maxHeight = "0px";
    document.body.appendChild(container);
    containerRef.current = container;

    widgetIdRef.current = globalNamespace.turnstile.render(container, {
      "sitekey": TURNSTILE_SITE_KEY,
      "callback": (token) => onTurnstileResponse(token),
      "error-callback": () => onTurnstileResponse(null),
      "expired-callback": () => onTurnstileResponse(null),
      "appearance": "execute",
      "execution": "execute",
      "size": "invisible",
    });

    return () => {
      // Handle currently executing challenge
      if (currentExecutionRef.current) {
        const originalResolve = currentExecutionRef.current.resolve;
        currentExecutionRef.current.resolve = (token: string | null) => {
          originalResolve(token);
          cleanup();
        };
      }

      // Handle queued challenges
      while (executionQueueRef.current.length > 0) {
        const execution = executionQueueRef.current.shift();
        if (execution) {
          // Create and immediately resolve a new promise with null
          new Promise<string | null>((resolve) => resolve(null)).then(execution);
        }
      }

      isExecutingRef.current = false;

      // If no current execution, clean up immediately
      if (!currentExecutionRef.current) {
        cleanup();
      }

      function cleanup() {
        if (containerRef.current && document.body.contains(containerRef.current)) {
          if (widgetIdRef.current && globalNamespace.turnstile) {
            try {
              globalNamespace.turnstile.remove(widgetIdRef.current);
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error("Error removing Turnstile widget:", e);
            }
          }
          document.body.removeChild(containerRef.current);
          containerRef.current = null;
          widgetIdRef.current = null;
        }
      }
    };
  }, [isTurnstileLoaded, onTurnstileResponse]);

  const executeSingleTurnstile = useCallback(async (): Promise<string | null> => {
    if (!isTurnstileLoaded || !globalNamespace.turnstile || !widgetIdRef.current) {
      return null;
    }

    try {
      globalNamespace.turnstile.reset(widgetIdRef.current);

      return new Promise<string | null>((resolve) => {
        const timeout = setTimeout(() => {
          onTurnstileResponse(null);
        }, 10_000);

        currentExecutionRef.current = {
          resolve,
          timeout,
        };

        if (widgetIdRef.current) {
          globalNamespace.turnstile?.execute(widgetIdRef.current);
        }
      });
    } catch {
      cleanupExecution();
      processNextExecution();
      return null;
    }
  }, [isTurnstileLoaded, onTurnstileResponse, cleanupExecution, processNextExecution]);

  const executeTurnstile = useCallback(async (): Promise<string | null> => {
    return new Promise<string | null>((resolve) => {
      const execution = async () => {
        const result = await executeSingleTurnstile();
        resolve(result);
        return result;
      };

      executionQueueRef.current.push(execution);
      if (!isExecutingRef.current) {
        processNextExecution();
      }
    });
  }, [executeSingleTurnstile, processNextExecution]);

  const renderTurnstileWidget = useCallback(
    (container: HTMLDivElement, renderParameters: RenderParameters) => {
      if (!globalNamespace.turnstile) {
        return null;
      }
      return globalNamespace.turnstile.render(container, renderParameters);
    },
    [globalNamespace.turnstile],
  );

  return (
    <AntiBotVerificationContext.Provider
      value={{
        executeTurnstile,
        renderTurnstileWidget,
      }}
    >
      {children}
    </AntiBotVerificationContext.Provider>
  );
};

export const useAntiBotVerification = () => {
  const context = useContext(AntiBotVerificationContext);
  if (!context) {
    throw new Error("useAntiBotVerification must be used within a AntiBotVerificationProvider");
  }
  return context;
};
